import React, { useContext } from "react";
import InterfaceContext, { SupportedLanguage } from "./Context";
import { DataTable } from "@aidkitorg/component-library";
import { ColumnDef } from "@tanstack/react-table";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { Dropdown } from "./Components/Dropdown";
import { Translations } from "@aidkitorg/i18n/lib";
import { useLocalizedStrings } from "./Localization";
import { getStageFromRequest, SupportRequest, TableKey } from "./SupportUtil";

type SupportTableV3Props = {
  columns: ColumnDef<any>[];
  data: SupportRequest[];
  channel: string;
  getOrCreateSupportCase: (row: any, leaveUnassigned?: boolean) => Promise<string>;
  makeSupportCaseUrl: (channel: string, caseId: string) => string;
  loading: boolean;
};

function langForDate(lang: SupportedLanguage) {
  return lang.replaceAll('_', '-');
}

const maskSystemUser = (row: any, field: { key: string }) => {
  const value = row[field.key] || '';
  if(value?.toLowerCase?.() === 'system' && field.key === 'assigned_to_name') {
    return '';
  }
  return value;
};

const localizeStageFn = (L: Translations) => (stage: string) => {
  switch (stage) {
    case "No Application":
      return L.application_stage.pre_application;
    case "In Progress":
      return L.application_stage.in_progress;
    case "Applied":
      return L.application_stage.applied;
    case "All Stages":
      return L.support.all_stages;
    default:
      return stage;
  }
}

export function SupportTableV3({
  columns,
  data,
  channel,
  getOrCreateSupportCase,
  makeSupportCaseUrl,
  loading
}: SupportTableV3Props) {
  const L = useLocalizedStrings();
  const localizeStage = localizeStageFn(L);
  const context = useContext(InterfaceContext);

  // Transform data for display
  const transformedData = data.map(request => {
    const row = request.row;
    const app = request.row.source;
    // format dates
    const case_created = row.case_created ? new Date(row.case_created).toLocaleString(langForDate(context.lang)) : null;
    const min = (row.min ? new Date(row.min).toLocaleString(langForDate(context.lang)) : null);
    const max = (row.max ? new Date(row.max).toLocaleString(langForDate(context.lang)) : null);
    // format to hide system user
    const assigned_to_name = maskSystemUser(row, { key: 'assigned_to_name' });
    // convert arrays to strings for global search and display
    const tags = Array.isArray(row.tags) ? row.tags.join(', ') : (row.tags || '');
    const contact_methods = Array.isArray(row.contact_methods) ? row.contact_methods.join(', ') : (row.contact_methods || '');
    // extract last message text for search and display
    const last_message_text = row.last_message_text || '';
    // other fields
    const unhandled_messages = (row.messages || []).length;
    const applicant_stage = localizeStage(getStageFromRequest(request));
    
    return { 
      id: app,
      ...row, 
      case_created, 
      min, 
      max, 
      last_message_text,
      assigned_to_name, 
      unhandled_messages, 
      applicant_stage,
      tags,
      contact_methods,
      request: request
    };
  });

  return (
    <DataTable
      columns={columns}
      data={transformedData}
      actions={[{
        label: 'View Case',
        onClick: async (row) => {
          let caseId = await getOrCreateSupportCase(row, true);
          window.open(makeSupportCaseUrl(channel, caseId), '_blank');
        }
      }, {
        label: 'Claim',
        onClick: async (row) => {
          await getOrCreateSupportCase(row, false);
        },
        hideWhen: (row) => !!row.support_case && row.assigned_to?.toLowerCase() !== 'system'
      }]}
      columnPinningState={{
        right: ['actions']
      }}
      initializing={loading}
      enableSearch={true}
    />
  )
}